/**
 * News API utility functions
 * Provides methods for fetching news, categories, locations from backend
 */

import { API_BASE_URL as API_URL } from './config';

// ==================== NEWS API ===========================

export const newsAPI = {
  // Get all news with filters
  getNews: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.category) params.append('category', filters.category);
    if (filters.district) params.append('district', filters.district);
    if (filters.isBreaking) params.append('isBreaking', filters.isBreaking);
    if (filters.search) params.append('search', filters.search);

    const response = await fetch(`${API_URL}/news?${params.toString()}`);
    return response.json();
  },

  // Get news by slug
  getNewsBySlug: async (slug) => {
    const response = await fetch(`${API_URL}/news/${slug}`);
    return response.json();
  },

  // Get news by id
  getNewsById: async (id) => {
    const response = await fetch(`${API_URL}/news/id/${id}`);
    return response.json();
  },

  // Increment news view count
  incrementNewsView: async (id) => {
    const response = await fetch(`${API_URL}/news/id/${id}/view`, {
      method: 'POST'
    });
    return response.json();
  },

  // Increment news share count
  incrementNewsShare: async (id) => {
    const response = await fetch(`${API_URL}/news/id/${id}/share`, {
      method: 'POST'
    });
    return response.json();
  },

  // Get breaking news
  getBreakingNews: async (limit = 5) => {
    const response = await fetch(`${API_URL}/news/breaking?limit=${limit}`);
    return response.json();
  },

  // Get trending news
  getTrendingNews: async (limit = 10, days = 7) => {
    const response = await fetch(`${API_URL}/news/trending?limit=${limit}&days=${days}`);
    return response.json();
  },

  // Get news by category
  getNewsByCategory: async (categorySlug, page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/news/category/${categorySlug}?page=${page}&limit=${limit}`
    );
    return response.json();
  },

  // Get news by district
  getNewsByDistrict: async (district, page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/news/district/${district}?page=${page}&limit=${limit}`
    );
    return response.json();
  },

  // Get news by block
  getNewsByBlock: async (block, page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/news/block/${block}?page=${page}&limit=${limit}`
    );
    return response.json();
  },

  // Get news by village
  getNewsByVillage: async (village, page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/news/village/${village}?page=${page}&limit=${limit}`
    );
    return response.json();
  },

  // Search news
  searchNews: async (query, page = 1, limit = 10) => {
    const response = await fetch(
      `${API_URL}/news/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
    );
    return response.json();
  },

  // Get related news
  getRelatedNews: async (newsId, limit = 5) => {
    const response = await fetch(`${API_URL}/news/related/${newsId}?limit=${limit}`);
    return response.json();
  },

  // Get latest news for homepage
  getLatestNews: async (limit = 6) => {
    const response = await fetch(`${API_URL}/news/section/latest/get?limit=${limit}`);
    return response.json();
  },

  getAdminNews: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await fetch(`${API_URL}/news/admin/all?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.json();
  },

  deleteNews: async (id) => {
    const response = await fetch(`${API_URL}/news/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.json();
  }
};

// ==================== CATEGORY API ===========================

export const categoryAPI = {
  // Get all categories
  getCategories: async (parentOnly = false) => {
    const response = await fetch(
      `${API_URL}/categories?parentOnly=${parentOnly}`
    );
    return response.json();
  },

  // Get category by slug
  getCategoryBySlug: async (slug) => {
    const response = await fetch(`${API_URL}/categories/${slug}`);
    return response.json();
  },

  // Add new category
  addCategory: async (categoryData) => {
    const response = await fetch(`${API_URL}/categories/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(categoryData)
    });
    return response.json();
  },

  // Update category
  updateCategory: async (categoryId, categoryData) => {
    const response = await fetch(`${API_URL}/categories/${categoryId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(categoryData)
    });
    return response.json();
  },

  // Delete category
  deleteCategory: async (categoryId) => {
    const response = await fetch(`${API_URL}/categories/${categoryId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });
    return response.json();
  }
};

// ==================== LOCATION API ===========================

export const locationAPI = {
  // Districts
  getDistricts: async () => {
    const response = await fetch(`${API_URL}/locations/districts`);
    return response.json();
  },

  getDistrictBySlug: async (slug) => {
    const response = await fetch(`${API_URL}/locations/districts/${slug}`);
    return response.json();
  },

  // Blocks
  getBlocks: async (district = null) => {
    const url = district 
      ? `${API_URL}/locations/blocks?district=${district}`
      : `${API_URL}/locations/blocks`;
    const response = await fetch(url);
    return response.json();
  },

  getBlocksByDistrict: async (districtId) => {
    const response = await fetch(
      `${API_URL}/locations/blocks/district/${districtId}`
    );
    return response.json();
  },

  // Villages
  getVillages: async (block = null, district = null) => {
    const params = new URLSearchParams();
    if (block) params.append('block', block);
    if (district) params.append('district', district);

    const response = await fetch(`${API_URL}/locations/villages?${params.toString()}`);
    return response.json();
  },

  getVillagesByBlock: async (blockId) => {
    const response = await fetch(
      `${API_URL}/locations/villages/block/${blockId}`
    );
    return response.json();
  }
};

const apiExports = {
  newsAPI,
  categoryAPI,
  locationAPI
};

export default apiExports;
